import React from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import {
  SEO,
  Content,
  Text,
  Spacing,
  devices,
  Img,
} from '../components/common';
import BookingPortal from '../components/BookingPortal/BookingPortal';
import Accordion from '../components/Accordion';
import FaqSchema from '../components/common/FaqSchema';
import ContactForm from '../components/Contact/ContactForm';
import EmployeeListingSection from '../components/EmployeeListingSection';
import QuickLinksSection from '../components/Home/QuickLinksSection';
import { CampaignPage as CampaignPageType, CampaignTextSectionWithReadMore } from '../@types/types';
import { ReadMoreButton } from '../pages';
import { NextPage } from '../components/SVG';
import { RichTextContent } from '../components/StyledComponents';

const DEFAULT_BLOCK_ORDER = [
  'QuickLinksSection',
  'Intro',
  'BookingWidget',
  'TextWithCalendarIcon',
  'CheckmarksList',
  'SecondCheckmarksList',
  'TextWithPlacemarkIcon',
  'TextWithImage',
  'SecondTextWithImage',
  'ThirdTextWithImage',
  'FourthTextWithImage',
  'FifthTextWithImage',
  'CardCarousel',
  'CollapsibleAccordion',
  'SecondCollapsibleAccordion',
  'ReadMoreWithLink',
  'ContactForm',
];

/** Campaign sections use Strapi FontColor / BackgroundColor; font defaults to black. */
const DEFAULT_SECTION_FONT = '#000000';

const hasBg = (c?: string) => c != null && c !== '';

const sectionFont = (c?: string) => c ?? DEFAULT_SECTION_FONT;

type Props = {
  data: {
    campaignPage: CampaignPageType;
    placemark: { publicURL: string };
    calendar: { publicURL: string };
    checkmark: { publicURL: string };
  };
  location: Location;
};

const CampaignTemplate = ({ data, location }: Props) => {
  const { campaignPage: page, placemark, calendar, checkmark } = data;

  const blockOrder =
    page.ContentSectionOrder?.length > 0
      ? page.ContentSectionOrder.map((item) => item.Section)
      : DEFAULT_BLOCK_ORDER;

  const renderReadMoreWithLink = (
    key: string,
    section?: CampaignTextSectionWithReadMore
  ) => {
    if (!section) return null;
    const showReadMore =
      section.ReadMoreLinkUrl && section.ReadMoreLinkLabel;
    const pictureOnLeft = section.ShowPictureOn === 'Left';
    const pictureUrl = section.Picture?.publicURL;
    return (
      <SectionContainer
        key={key}
        hasImage={!!pictureUrl}
        backgroundColor={section.BackgroundColor}
        fontColor={sectionFont(section.FontColor)}
        lightestBlue={!hasBg(section.BackgroundColor)}
      >
        {pictureOnLeft && pictureUrl && (
          <ImageContainer src={pictureUrl} $edge="left" />
        )}
        <TextSection>
          <Text variant="title-2" color="inherit">
            {section.Title}
          </Text>
          <Spacing amount="medium" axis="y" />
          <RichTextContent>{section.Text}</RichTextContent>
          {showReadMore && (
            <>
              <Spacing amount="medium" axis="y" />
              <ReadMoreButton
                href={section.ReadMoreLinkUrl!}
                target="_blank"
                rel="noopener noreferrer"
              >
                {section.ReadMoreLinkLabel}
                <Spacing axis="x" amount="medium" />
                <NextPage width="30" />
              </ReadMoreButton>
            </>
          )}
        </TextSection>
        {!pictureOnLeft && pictureUrl && (
          <ImageContainer src={pictureUrl} $edge="right" />
        )}
      </SectionContainer>
    );
  };

  const renderSection = (key: string) => {
    switch (key) {
      case 'Intro': {
        const section = page.Intro;
        if (!section) return null;
        const pictureOnLeft = section.ShowPictureOn === 'Left';
        const pictureUrl = section.Picture?.publicURL;
        return (
          <SectionContainer
            key={key}
            hasImage={!!pictureUrl}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="left" />
            )}
            <TextSection>
              <Text variant="title-1" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              <RichTextContent>{section.Text}</RichTextContent>
            </TextSection>
            {!pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="right" />
            )}
          </SectionContainer>
        );
      }
      case 'BookingWidget': {
        const settings = page.BookingWidget;
        return (
          <BookingPortalContainer
            key={key}
            backgroundColor={settings?.BackgroundColor}
            fontColor={sectionFont(undefined)}
          >
            <BookingPortal
              preselectionUrl={settings?.NordHealthBookingUrl}
              enableTimeRangeFilter={settings?.ShowHourRangeFilters === 'Yes'}
            />
          </BookingPortalContainer>
        );
      }
      case 'TextWithCalendarIcon': {
        const section = page.TextWithCalendarIcon;
        if (!section) return null;
        const pictureOnLeft = section.ShowPictureOn === 'Left';
        const pictureUrl = section.Picture?.publicURL;
        return (
          <SectionContainer
            key={key}
            hasImage={!!pictureUrl}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="left" />
            )}
            <TextSection>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              <TextContainerWithIcon>
                <Icon
                  file={{ url: calendar.publicURL }}
                  description="calendar"
                />
                <RichTextContent>{section.Text}</RichTextContent>
              </TextContainerWithIcon>
            </TextSection>
            {!pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="right" />
            )}
          </SectionContainer>
        );
      }
      case 'CheckmarksList':
      case 'SecondCheckmarksList': {
        const section = {
          CheckmarksList: page.CheckmarksList,
          SecondCheckmarksList: page.SecondCheckmarksList,
        }[key as 'CheckmarksList' | 'SecondCheckmarksList'];
        if (!section) return null;
        const pictureOnLeft = section.ShowPictureOn === 'Left';
        const pictureUrl = section.Picture?.publicURL;
        return (
          <SectionContainer
            key={key}
            hasImage={!!pictureUrl}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="left" />
            )}
            <TextSection
              style={{ gap: 28, display: 'flex', flexDirection: 'column' }}
            >
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              {section.Accordion.map((item, index) => (
                <TextContainerWithIcon key={index}>
                  <Icon
                    file={{ url: checkmark.publicURL }}
                    description="checkmark"
                    style={{ width: 40, height: 40 }}
                  />
                  <Container>
                    <BoldHeading>{item.Header}</BoldHeading>
                    <RichTextContent>{item.Content}</RichTextContent>
                  </Container>
                </TextContainerWithIcon>
              ))}
            </TextSection>
            {!pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="right" />
            )}
          </SectionContainer>
        );
      }
      case 'TextWithPlacemarkIcon': {
        const section = page.TextWithPlacemarkIcon;
        if (!section) return null;
        const pictureOnLeft = section.ShowPictureOn === 'Left';
        const pictureUrl = section.Picture?.publicURL;
        return (
          <SectionContainer
            key={key}
            hasImage={!!pictureUrl}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="left" />
            )}
            <TextSection>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              <TextContainerWithIcon>
                <Icon
                  file={{ url: placemark.publicURL }}
                  description="placemark"
                />
                <RichTextContent>{section.Text}</RichTextContent>
              </TextContainerWithIcon>
            </TextSection>
            {!pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="right" />
            )}
          </SectionContainer>
        );
      }
      case 'TextWithImage':
      case 'SecondTextWithImage':
      case 'ThirdTextWithImage':
      case 'FourthTextWithImage':
      case 'FifthTextWithImage': {
        const section = {
          TextWithImage: page.TextWithImage,
          SecondTextWithImage: page.SecondTextWithImage,
          ThirdTextWithImage: page.ThirdTextWithImage,
          FourthTextWithImage: page.FourthTextWithImage,
          FifthTextWithImage: page.FifthTextWithImage,
        }[key as
          | 'TextWithImage'
          | 'SecondTextWithImage'
          | 'ThirdTextWithImage'
          | 'FourthTextWithImage'
          | 'FifthTextWithImage'];
        if (!section) return null;
        const pictureOnLeft = section.ShowPictureOn === 'Left';
        const pictureUrl = section.Picture?.publicURL;
        return (
          <SectionContainer
            key={key}
            hasImage={!!pictureUrl}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="left" />
            )}
            <TextSection>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              <RichTextContent>{section.Text}</RichTextContent>
            </TextSection>
            {!pictureOnLeft && pictureUrl && (
              <ImageContainer src={pictureUrl} $edge="right" />
            )}
          </SectionContainer>
        );
      }
      case 'CardCarousel': {
        const section = page.CardCarousel;
        if (!section) return null;
        const cardBg = section.CardBackgroundColor;
        const cardFg = section.CardFontColor;
        return (
          <SectionContainer
            key={key}
            hasImage={false}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            <TextSection>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              <CustomerCardRow>
                {section.Accordion.map((item, index) => (
                  <CustomerCard
                    key={index}
                    backgroundColor={cardBg}
                    fontColor={sectionFont(cardFg)}
                  >
                    <BoldHeading>{item.Header}</BoldHeading>
                    <RichTextContent>{item.Content}</RichTextContent>
                  </CustomerCard>
                ))}
              </CustomerCardRow>
            </TextSection>
          </SectionContainer>
        );
      }
      case 'CollapsibleAccordion':
      case 'SecondCollapsibleAccordion': {
        const section = {
          CollapsibleAccordion: page.CollapsibleAccordion,
          SecondCollapsibleAccordion: page.SecondCollapsibleAccordion,
        }[key as 'CollapsibleAccordion' | 'SecondCollapsibleAccordion'];
        if (!section) return null;
        return (
          <SectionContainer
            key={key}
            hasImage={false}
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
            lightestBlue={!hasBg(section.BackgroundColor)}
          >
            {section.ContainsFrequentlyAskedQuestions && (
              <FaqSchema items={section.Accordion} />
            )}
            <TextSection>
              <Text variant="title-2" color="inherit">
                {section.Title}
              </Text>
              <Spacing amount="medium" axis="y" />
              {section.Accordion.map((item, i) => (
                <Accordion key={i} header={item.Header} text={item.Content} />
              ))}
            </TextSection>
          </SectionContainer>
        );
      }
      case 'ReadMoreWithLink':
        return renderReadMoreWithLink(key, page.ReadMoreWithLink);
      case 'SecondReadMoreWithLink':
        return renderReadMoreWithLink(key, page.SecondReadMoreWithLink);
      case 'ThirdReadMoreWithLink':
        return renderReadMoreWithLink(key, page.ThirdReadMoreWithLink);
      case 'ContactForm': {
        const section = page.ContactForm;
        if (!section) return null;
        return (
          <ContactSection
            key={key}
            id="contactForm"
            backgroundColor={section.BackgroundColor}
            fontColor={sectionFont(section.FontColor)}
          >
            <ContactInfoContainer>
              <Text variant="title-2" align="left" color="inherit">
                {section.ContactFormTitle}
              </Text>
              {section.ContactFormSubTitle && (
                <Text variant="title-3" align="left" color="inherit">
                  {section.ContactFormSubTitle}
                </Text>
              )}
              <Spacing axis="y" amount="medium" />
              <RichTextContent>{section.ContactFormContent}</RichTextContent>
              <Spacing axis="y" amount="xlarge" />
              <ContactForm
                about={(section.ContactingAboutOptions ?? []).map(
                  (i) => i.Item
                )}
                align="left"
                allowEmail={section.AllowEmail === true}
              />
            </ContactInfoContainer>
          </ContactSection>
        );
      }
      case 'EmployeeListing': {
        const section = page.EmployeeListing;
        if (!section) return null;
        const listingEmployees = page.employees ?? [];
        return (
          <EmployeeListingSection
            key={key}
            section={section}
            employees={listingEmployees}
          />
        );
      }
      case 'QuickLinksSection': {
        return (
          <QuickLinksSection
            key={key}
            title={page.QuickLinksSection?.QuickLinksTitle}
            links={page.QuickLinksSection?.QuickLinks ?? []}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        pathname={location.pathname}
        title={page.MetaData?.MetaTitle || 'Kampanja'}
        description={page.MetaData?.MetaDescription || ''}
        keywords={page.MetaData?.metaKeywords}
      />
      {blockOrder.map((key) => renderSection(key))}
    </>
  );
};

export default CampaignTemplate;

export const query = graphql`
  query CampaignPageBySlug($slug: String!) {
    campaignPage: strapiCampaignPage(slug: { eq: $slug }) {
      slug
      MetaData {
        MetaTitle
        MetaDescription
        metaKeywords
      }
      QuickLinksSection {
        QuickLinksTitle
        QuickLinks {
          Title
          Url
        }
      }
      BookingWidget {
        NordHealthBookingUrl
        ShowHourRangeFilters
        BackgroundColor
      }
      ContentSectionOrder {
        Section
      }
      Intro {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      TextWithCalendarIcon {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      CheckmarksList {
        Title
        Accordion {
          Header
          Content
        }
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      SecondCheckmarksList {
        Title
        Accordion {
          Header
          Content
        }
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      TextWithPlacemarkIcon {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      TextWithImage {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      SecondTextWithImage {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      ThirdTextWithImage {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      FourthTextWithImage {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      FifthTextWithImage {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        BackgroundColor
        FontColor
      }
      CardCarousel {
        Title
        Accordion {
          Header
          Content
        }
        BackgroundColor
        FontColor
        CardBackgroundColor
        CardFontColor
      }
      CollapsibleAccordion {
        Title
        Accordion {
          Header
          Content
        }
        BackgroundColor
        FontColor
        ContainsFrequentlyAskedQuestions
      }
      SecondCollapsibleAccordion {
        Title
        Accordion {
          Header
          Content
        }
        BackgroundColor
        FontColor
        ContainsFrequentlyAskedQuestions
      }
      ReadMoreWithLink {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        ReadMoreLinkUrl
        ReadMoreLinkLabel
        BackgroundColor
        FontColor
      }
      SecondReadMoreWithLink {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        ReadMoreLinkUrl
        ReadMoreLinkLabel
        BackgroundColor
        FontColor
      }
      ThirdReadMoreWithLink {
        Title
        Text
        Picture {
          publicURL
        }
        ShowPictureOn
        ReadMoreLinkUrl
        ReadMoreLinkLabel
        BackgroundColor
        FontColor
      }
      ContactForm {
        ContactFormTitle
        ContactFormSubTitle
        ContactFormContent
        AllowEmail
        ContactingAboutOptions {
          Item
        }
        BackgroundColor
        FontColor
      }
      EmployeeListing {
        Title
        Text
        BackgroundColor
        FontColor
      }
      employees {
        ...strapiEmployeesFragment
      }
    }
    placemark: file(name: { eq: "placemark" }) {
      publicURL
    }
    calendar: file(name: { eq: "calendar" }) {
      publicURL
    }
    checkmark: file(name: { eq: "checkmark" }) {
      publicURL
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactSection = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.theme.colors.lighterBlue};
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
`;

const ContactInfoContainer = styled(Content)`
  word-break: normal;
  overflow-wrap: anywhere;
  margin-left: 80px;
  @media ${devices.tablet} {
    margin: auto;
  }
`;

const TextContainerWithIcon = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  align-items: center;
`;

const Icon = styled(Img)`
  align-self: center;
`;

const BoldHeading = styled.span`
  font-weight: bold;
  color: inherit;
`;

const SectionContainer = styled.div<{
  hasImage?: boolean;
  lightestBlue?: boolean;
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.lightestBlue
      ? props.theme.colors.lightestBlue
      : props.theme.colors.lighterBlue};
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  height: max-content;

  @media ${devices.laptop} {
    grid-template-columns: 1fr;
  }

  @media ${devices.mobileXL} {
    height: auto;
    display: block;
  }
`;

const BookingPortalContainer = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 80px;
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.theme.colors.lightestBlue};
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  @media ${devices.tablet} {
    padding: ${(props) => props.theme.spacing.default};
  }
`;

const TextSection = styled.div`
  padding: 40px 80px;
  height: auto;
  display: block;
  flex: 1;
  min-width: 0;

  @media ${devices.tablet} {
    padding: ${(props) => props.theme.spacing.default};
  }

  @media ${devices.mobileXL} {
    width: 95%;
  }
`;

const ImageContainer = styled.div<{ src: string; $edge: 'left' | 'right' }>`
  width: 450px;
  height: 600px;
  flex-shrink: 0;
  align-self: center;
  box-sizing: border-box;
  overflow: hidden;
  aspect-ratio: 3 / 4;
  background-image: url(${(props) => props.src});
  background-position: ${(props) =>
    props.$edge === 'left' ? 'left center' : 'right center'};
  background-repeat: no-repeat;
  background-size: cover;
  ${(props) => props.theme.imageTransition};

  @media ${devices.laptop} {
    display: none;
  }
`;

const CustomerCardRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  @media ${devices.tablet} {
    flex-direction: column;
  }
`;

const CustomerCard = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
}>`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.fontColor ?? DEFAULT_SECTION_FONT};
  background-color: ${(props) =>
    hasBg(props.backgroundColor)
      ? props.backgroundColor
      : props.theme.colors.lightestBlue};
  box-shadow: 0px 5px 15px rgba(2, 38, 64, 0.1);
  border-radius: 28px;
  padding: 28px;
  gap: 8px;
`;
